import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// ── DESIGN TOKENS ──
const COLORS = {
  lowRisk: "#10b981",
  moderateRisk: "#f59e0b",
  highRisk: "#ef4444",
  route: "#06b6d4",
  cardBorder: "#e2e8f0",
  onDark: "rgba(255,255,255,0.90)",
};

const MAP_CENTER: [number, number] = [15, 20];
const CAR_CENTER = { lat: 6.6111, lng: 20.9394 };
const CAR_COORDINATES: [number, number] = [CAR_CENTER.lat, CAR_CENTER.lng];

/* ═══════════════════════════════════════════════════════════════════
   UTILITIES
══════════════════════════════════════════════════════════════════ */

// Country centroids (comprehensive)
const COUNTRY_CENTROIDS: Record<string, { lat: number; lng: number; name: string }> = {
  "guinea": { lat: 9.9456, lng: -9.6966, name: "Guinea" },
  "sierra leone": { lat: 8.4606, lng: -11.7799, name: "Sierra Leone" },
  "liberia": { lat: 6.4281, lng: -9.4295, name: "Liberia" },
  "democratic republic of the congo": { lat: -4.0383, lng: 21.7587, name: "Democratic Republic of Congo" },
  "uganda": { lat: 1.3733, lng: 32.2903, name: "Uganda" },
  "kenya": { lat: -0.0236, lng: 37.9062, name: "Kenya" },
  "nigeria": { lat: 9.0820, lng: 8.6753, name: "Nigeria" },
  "cameroon": { lat: 3.8480, lng: 11.5021, name: "Cameroon" },
  "south sudan": { lat: 6.8770, lng: 31.3070, name: "South Sudan" },
  "ethiopia": { lat: 9.1450, lng: 40.4897, name: "Ethiopia" },
  "malawi": { lat: -13.2543, lng: 34.3015, name: "Malawi" },
  "mozambique": { lat: -18.6657, lng: 35.5296, name: "Mozambique" },
  "tanzania": { lat: -6.3690, lng: 34.8888, name: "Tanzania" },
  "zambia": { lat: -13.1339, lng: 27.8493, name: "Zambia" },
  "zimbabwe": { lat: -19.0154, lng: 29.1549, name: "Zimbabwe" },
  "south africa": { lat: -30.5595, lng: 22.9375, name: "South Africa" },
  "angola": { lat: -11.2027, lng: 17.8739, name: "Angola" },
  "congo": { lat: -4.0383, lng: 21.7587, name: "Congo" },
  "gabon": { lat: -0.8037, lng: 11.6045, name: "Gabon" },
  "central african republic": { lat: 6.6111, lng: 20.9394, name: "Central African Republic" },
  "chad": { lat: 15.4542, lng: 18.7322, name: "Chad" },
  "sudan": { lat: 12.8628, lng: 30.2176, name: "Sudan" },
  "egypt": { lat: 26.8206, lng: 30.8025, name: "Egypt" },
  "ghana": { lat: 7.3697, lng: -5.5825, name: "Ghana" },
  "ivory coast": { lat: 7.5400, lng: -5.5471, name: "Côte d'Ivoire" },
  "benin": { lat: 9.3077, lng: 2.3158, name: "Benin" },
  "togo": { lat: 8.6753, lng: 0.8235, name: "Togo" },
  "mali": { lat: 17.5707, lng: -3.9962, name: "Mali" },
  "burkina faso": { lat: 12.2383, lng: -1.5616, name: "Burkina Faso" },
  "niger": { lat: 17.6078, lng: 8.6753, name: "Niger" },
  "senegal": { lat: 14.4974, lng: -14.4524, name: "Senegal" },
  "france": { lat: 46.2276, lng: 2.2137, name: "France" },
  "united states": { lat: 37.0902, lng: -95.7129, name: "United States" },
  "united kingdom": { lat: 55.3781, lng: -3.4360, name: "United Kingdom" },
  "canada": { lat: 56.1304, lng: -106.3468, name: "Canada" },
  "belgium": { lat: 50.5039, lng: 4.4699, name: "Belgium" },
  "netherlands": { lat: 52.1326, lng: 5.2913, name: "Netherlands" },
  "germany": { lat: 51.1657, lng: 10.4515, name: "Germany" },
  "italy": { lat: 41.8719, lng: 12.5674, name: "Italy" },
  "spain": { lat: 40.4637, lng: -3.7492, name: "Spain" },
  "switzerland": { lat: 46.8182, lng: 8.2275, name: "Switzerland" },
  "australia": { lat: -25.2744, lng: 133.7751, name: "Australia" },
  "india": { lat: 20.5937, lng: 78.9629, name: "India" },
  "china": { lat: 35.8617, lng: 104.1954, name: "China" },
  "japan": { lat: 36.2048, lng: 138.2529, name: "Japan" },
  "thailand": { lat: 15.8700, lng: 100.9925, name: "Thailand" },
  "singapore": { lat: 1.3521, lng: 103.8198, name: "Singapore" },
};

// Get country centroid
const getCountryCentroid = (country: string): { lat: number; lng: number } | null => {
  const normalized = (country || "").toLowerCase().trim();
  const match = Object.entries(COUNTRY_CENTROIDS).find(
    ([key]) => normalized.includes(key) || key.includes(normalized)
  );
  return match ? { lat: match[1].lat, lng: match[1].lng } : null;
};

const getRiskLevel = (risk?: string) => {
  const normalized = (risk || "").toLowerCase().trim();
  if (normalized.includes("high")) return "High Risk";
  if (normalized.includes("moderate")) return "Moderate Risk";
  return "Low Risk";
};

const getRiskColor = (risk?: string) => {
  const normalized = (risk || "").toLowerCase().trim();
  if (normalized.includes("high")) return COLORS.highRisk;
  if (normalized.includes("moderate")) return COLORS.moderateRisk;
  return COLORS.lowRisk;
};

const getMarkerRadius = (count?: number) => Math.max(5, Math.min(18, 4 + (count || 1) * 1.5));

type Hotspot = {
  country: string;
  countryName: string;
  coords: { lat: number; lng: number };
  count: number;
  riskLevel: string;
  color: string;
  radius: number;
};

// Generate great circle arc between two points (simplified)
const generateArc = (from: { lat: number; lng: number }, to: { lat: number; lng: number }, steps = 50) => {
  const coords: [number, number][] = [];
  try {
    for (let i = 0; i <= steps; i++) {
      const f = i / steps;
      const lat1 = (from.lat * Math.PI) / 180;
      const lon1 = (from.lng * Math.PI) / 180;
      const lat2 = (to.lat * Math.PI) / 180;
      const lon2 = (to.lng * Math.PI) / 180;

      const dLon = lon2 - lon1;
      const a = Math.sin((1 - f) * Math.acos(Math.sin(lat1) * Math.sin(lat2) + Math.cos(lat1) * Math.cos(lat2) * Math.cos(dLon))) /
                Math.sin(Math.acos(Math.sin(lat1) * Math.sin(lat2) + Math.cos(lat1) * Math.cos(lat2) * Math.cos(dLon)) || 1);
      const b = Math.sin((f * Math.acos(Math.sin(lat1) * Math.sin(lat2) + Math.cos(lat1) * Math.cos(lat2) * Math.cos(dLon))) || 0) /
                Math.sin(Math.acos(Math.sin(lat1) * Math.sin(lat2) + Math.cos(lat1) * Math.cos(lat2) * Math.cos(dLon)) || 1);

      const x = a * Math.cos(lat1) * Math.cos(lon1) + b * Math.cos(lat2) * Math.cos(lon2);
      const y = a * Math.cos(lat1) * Math.sin(lon1) + b * Math.cos(lat2) * Math.sin(lon2);
      const z = a * Math.sin(lat1) + b * Math.sin(lat2);

      const lat = Math.atan2(z, Math.sqrt(x * x + y * y)) * (180 / Math.PI);
      const lon = Math.atan2(y, x) * (180 / Math.PI);
      coords.push([lon, lat]);
    }
  } catch (e) {
    // Fallback: straight line
    return [[from.lng, from.lat], [to.lng, to.lat]];
  }
  return coords;
};

type Alert = {
  port?: string;
  country_of_origin?: string;
  risk_level?: string;
  timestamp?: string;
  alert_text?: string;
  [key: string]: any;
};

type MapPoint = {
  country?: string;
  risk?: string;
  count?: number;
  latitude?: number;
  longitude?: number;
};

type Props = {
  alerts?: Alert[];
  selectedCountry?: string;
  points?: MapPoint[];
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const GeoDashboardMap: React.FC<Props> = ({ points = [] }) => {
  const hotspots = useMemo<Hotspot[]>(() => {
    return (
      points
        .map((point) => {
          const country = point.country;
          if (!country) return null;

          const coords = getCountryCentroid(country);
          if (!coords) return null;

          const count = point.count && point.count > 0 ? point.count : 1;
          const riskLevel = getRiskLevel(point.risk);
          const color = getRiskColor(point.risk);
          const radius = getMarkerRadius(count);
          const normalized = country.toLowerCase().trim();

          return {
            country: normalized,
            countryName: COUNTRY_CENTROIDS[normalized]?.name || country,
            coords,
            count,
            riskLevel,
            color,
            radius,
          };
        })
        .filter((item): item is Hotspot => Boolean(item))
    );
  }, [points]);

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative", borderRadius: "16px", overflow: "hidden" }}>
      <MapContainer center={MAP_CENTER} zoom={2} scrollWheelZoom={false} style={{ width: "100%", height: "100%" }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {hotspots.map((hotspot) => (
          <CircleMarker
            key={`marker-${hotspot.country}`}
            center={[hotspot.coords.lat, hotspot.coords.lng]}
            radius={hotspot.radius}
            pathOptions={{
              color: hotspot.color,
              fillColor: hotspot.color,
              fillOpacity: 0.7,
              weight: 1,
            }}
          >
            <Tooltip direction="top" offset={[0, -8]} opacity={1}>
              <div style={{ minWidth: 140, color: "#fff", fontSize: 12, lineHeight: 1.4 }}>
                <strong>{hotspot.countryName}</strong>
                <br />
                Traveler Count: {hotspot.count}
                <br />
                Risk Level: {hotspot.riskLevel}
              </div>
            </Tooltip>
          </CircleMarker>
        ))}

        {hotspots.map((hotspot) => (
          <Polyline
            key={`route-${hotspot.country}`}
            positions={[[hotspot.coords.lat, hotspot.coords.lng], CAR_COORDINATES]}
            pathOptions={{
              color: COLORS.route,
              weight: 2,
              opacity: 0.65,
              dashArray: "8",
            }}
          />
        ))}
      </MapContainer>

      <Box
        sx={{
          position: "absolute",
          bottom: 20,
          left: 20,
          background: "rgba(15, 23, 42, 0.85)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(148, 163, 184, 0.2)",
          borderRadius: "12px",
          p: 1.5,
          minWidth: 180,
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: COLORS.onDark,
            mb: 1,
            opacity: 0.7,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Legend
        </Typography>
        {[
          { color: COLORS.lowRisk, label: "Low Risk" },
          { color: COLORS.moderateRisk, label: "Moderate Risk" },
          { color: COLORS.highRisk, label: "High Risk" },
          { color: COLORS.route, label: "Travel Routes" },
        ].map((item) => (
          <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
            {item.label === "Travel Routes" ? (
              <Box sx={{ width: 16, height: 2, background: item.color, borderRadius: "1px" }} />
            ) : (
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: item.color, flexShrink: 0 }} />
            )}
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>{item.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default GeoDashboardMap;
